import { Fragment, useEffect, useState, type CSSProperties } from "react";
import { Icons } from "../components/Icons.js";
import { AMBIANCE_LIST, AMBIANCES, type Mode } from "../theme/ambiances.js";
import type { Appearance, InterfaceMode, RecordingQuality } from "../theme/useAppearance.js";
import { api, type Preferences, type SettingsLimits, type UsageData } from "../lib/api.js";
import { UsageHeader } from "../settings/UsageHeader.js";
import { TrashPanel } from "../settings/TrashPanel.js";
import { MicTest } from "../settings/MicTest.js";

/* ---------- petits composants de formulaire ---------- */
function SettingSlider({ label, value, min, max, step, onChange, fmt }: { label: string; value: number; min: number; max: number; step: number; onChange: (v: number) => void; fmt?: (v: number) => string }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="set-slider">
      <div className="set-slider-top">
        <span className="set-label">{label}</span>
        <span className="set-slider-val">{fmt ? fmt(value) : value}</span>
      </div>
      <input type="range" className="set-range" min={min} max={max} step={step} value={value} style={{ "--pct": pct + "%" } as CSSProperties} onChange={(e) => onChange(parseFloat(e.target.value))} />
    </div>
  );
}
function CheckRow({ label, desc, checked, onChange }: { label: string; desc?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button className={"checkrow" + (checked ? " on" : "")} role="checkbox" aria-checked={checked} onClick={() => onChange(!checked)}>
      <span className="checkbox">{checked && <Icons.Check size={14} />}</span>
      <span className="checkrow-tx">
        <span className="set-label">{label}</span>
        {desc && <span className="set-desc">{desc}</span>}
      </span>
    </button>
  );
}
function PromptField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="promptfield">
      <label className="set-label">{label}</label>
      <textarea className="prompt-ta" rows={3} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

const BG_PRESETS: { id: string; label: string; css: string }[] = [
  { id: "", label: "Aucune", css: "" },
  { id: "aube", label: "Aube", css: "linear-gradient(160deg,#dbe6ff,#eceaff 55%,#f6e6f6)" },
  { id: "brume", label: "Brume", css: "linear-gradient(160deg,#d6efe6,#eef6f0 60%,#fcf3e6)" },
  { id: "nuit", label: "Nuit", css: "linear-gradient(160deg,#2b3553,#161d31 70%,#0d1220)" },
];

function pickImage(cb: (css: string) => void): void {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = () => {
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => cb(`url("${reader.result as string}")`);
    reader.readAsDataURL(file);
  };
  input.click();
}

function BgPicker({ value, onChange }: { value: string; onChange: (css: string) => void }) {
  const isCustom = !!value && value.startsWith("url");
  return (
    <div className="bg-grid">
      {BG_PRESETS.map((b) => (
        <button key={b.id} className={"bgopt" + (!isCustom && value === b.css ? " on" : "")} onClick={() => onChange(b.css)}>
          <span className="bgopt-sw" style={{ background: b.css || "var(--surface2)" }}>
            {b.id === "" ? <Icons.Close size={18} /> : !isCustom && value === b.css && <Icons.Check size={16} />}
          </span>
          <span className="amb-name">{b.label}</span>
        </button>
      ))}
      <button className={"bgopt" + (isCustom ? " on" : "")} onClick={() => pickImage(onChange)}>
        <span className="bgopt-sw" style={isCustom ? { backgroundImage: value, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
          {!isCustom && <Icons.Plus size={20} />}
        </span>
        <span className="amb-name">Photo</span>
      </button>
    </div>
  );
}

export interface SettingsContentProps {
  appearance: Appearance;
  onLogout: () => void;
  full: boolean;
  usage: UsageData | null;
  onChanged: () => void;
}

/**
 * Corps des paramètres (cahier des charges §12). En-tête de quotas, apparence et affichage (locaux à
 * l'appareil) toujours présents ; sur ordinateur (`full`), s'ajoutent les résumés automatiques, les
 * rapports/e-mails, les prompts, la rétention, la corbeille et la zone de danger (mobile = sous-
 * ensemble restreint, §12.3).
 */
export function SettingsContent({ appearance, onLogout, full, usage, onChanged }: SettingsContentProps) {
  const a = appearance;
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [limits, setLimits] = useState<SettingsLimits | null>(null);
  const [retentionDays, setRetentionDays] = useState(30);
  const [confirmText, setConfirmText] = useState("");
  const [resetMsg, setResetMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .settings()
      .then((s) => {
        if (cancelled) return;
        setPrefs(s.preferences);
        setLimits(s.limits);
        if (s.limits) setRetentionDays(s.limits.audioRetentionDays);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const setPref = <K extends keyof Preferences>(key: K, value: Preferences[K]) => {
    setPrefs((p) => (p ? { ...p, [key]: value } : p));
    const patch: Partial<Preferences> = {};
    (patch as Record<string, unknown>)[key as string] = value;
    api.updatePreferences(patch).catch(() => undefined);
  };
  const time5 = (t: string | null) => (t ? t.slice(0, 5) : "");

  const modeBtn = (m: Mode, label: string, icon: JSX.Element) => (
    <button className={"seg-b" + (a.mode === m ? " on" : "")} onClick={() => a.setMode(m)}>
      {icon} {label}
    </button>
  );

  // Bloc « Apparence » (thème, ambiance, fonds). Extrait dans une variable parce qu'il ne se place
  // pas au même endroit selon la version : juste avant la corbeille sur bureau, en fin de liste sur
  // mobile — où il n'y a ni corbeille ni zone de danger. Ce sont des réglages qu'on touche une fois,
  // d'où leur renvoi en bas, après les réglages de fonctionnement.
  const apparence = (
    <Fragment>
      <div className="set-section">Apparence</div>
      <div className="set-card">
        <div className="set-row set-row-col">
          <span className="set-label">Thème</span>
          <div className="seg seg3">
            {modeBtn("light", "Clair", <Icons.Sun size={16} />)}
            {modeBtn("dark", "Sombre", <Icons.Moon size={16} />)}
            {modeBtn("system", "Système", <Icons.Monitor size={16} />)}
          </div>
        </div>
      </div>
      <div className="set-card">
        <div className="set-row set-row-col">
          <span className="set-label">Ambiance</span>
          <div className="amb-grid">
            {AMBIANCE_LIST.map((k) => {
              const amb = AMBIANCES[k]!;
              return (
                <button key={k} className={"amb" + (a.ambiance === k ? " on" : "")} onClick={() => a.setAmbiance(k)}>
                  <span className="amb-sw" style={{ background: amb.swatch }}>{a.ambiance === k && <Icons.Check size={16} />}</span>
                  <span className="amb-name">{amb.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="set-card">
        <div className="set-row set-row-col">
          <span className="set-label">Image de fond</span>
          <BgPicker value={a.bgImage} onChange={a.setBgImage} />
        </div>
      </div>
      <div className="set-card">
        <div className="set-row set-row-col">
          <span className="set-label">Fond de la note cumulée</span>
          <BgPicker value={a.bgDaily} onChange={a.setBgDaily} />
        </div>
        <div className="set-divline" />
        <div className="set-row set-row-col">
          <span className="set-label">Fond de la note indépendante</span>
          <BgPicker value={a.bgStandalone} onChange={a.setBgStandalone} />
        </div>
      </div>
    </Fragment>
  );

  return (
    <Fragment>
      <div className="set-section">Quotas et décompte</div>
      <div className="set-card">
        <UsageHeader usage={usage} />
      </div>

      <div className="set-section">Affichage</div>
      <div className="set-card">
        <SettingSlider label="Taille des boutons" value={a.btnSize} min={0.85} max={1.4} step={0.05} onChange={a.setBtnSize} fmt={(v) => Math.round(v * 100) + " %"} />
        <div className="set-divline" />
        <SettingSlider label="Taille du texte" value={a.textSize} min={0.9} max={1.45} step={0.05} onChange={a.setTextSize} fmt={(v) => Math.round(v * 100) + " %"} />
        <div className="set-divline" />
        <SettingSlider
          label="Largeur des fenêtres (grand écran)"
          value={a.winMaxWidth}
          min={700}
          max={2400}
          step={50}
          onChange={(v) => a.setWinMaxWidth(Math.round(v))}
          fmt={(v) => `${Math.round(v)} px`}
        />
        <div className="set-divline" />
        <CheckRow
          label="Figer en mode étroit"
          desc="Garde l'interface compacte (mobile) même sur un grand écran."
          checked={a.forceNarrow}
          onChange={a.setForceNarrow}
        />
      </div>

      <div className="set-section">Capture</div>
      <div className="set-card">
        <MicTest deviceId={a.micDeviceId} onDeviceChange={a.setMicDeviceId} />
        <div className="set-divline" />
        <div className="set-row set-row-col">
          <span className="set-label">Interface</span>
          <div className="seg">
            {(["simple", "advanced"] as InterfaceMode[]).map((m) => (
              <button key={m} className={"seg-b" + (a.interfaceMode === m ? " on" : "")} onClick={() => a.setInterfaceMode(m)}>
                {m === "simple" ? "Simple" : "Avancée"}
              </button>
            ))}
          </div>
        </div>
        <div className="set-divline" />
        <div className="set-row set-row-col">
          <span className="set-label">Qualité d'enregistrement</span>
          <div className="seg seg3">
            {(["high", "medium", "low"] as RecordingQuality[]).map((q) => (
              <button key={q} className={"seg-b" + (a.quality === q ? " on" : "")} onClick={() => a.setQuality(q)}>
                {q === "high" ? "Haut" : q === "medium" ? "Moyen" : "Bas"}
              </button>
            ))}
          </div>
        </div>
        {prefs && (
          <>
            <div className="set-divline" />
            <CheckRow label="Afficher l'horodatage" desc="Précéder chaque entrée de la note du jour de son heure (## HH:MM)" checked={prefs.showTimestamps} onChange={(v) => setPref("showTimestamps", v)} />
            <div className="set-divline" />
            <CheckRow label="Analyser les photos (OCR)" desc="Extraire le texte des photos ajoutées" checked={prefs.analyzePhotos} onChange={(v) => setPref("analyzePhotos", v)} />
            <div className="set-divline" />
            <SettingSlider label="Délai de finalisation" value={prefs.finalizeDelaySeconds} min={3} max={60} step={1} onChange={(v) => setPref("finalizeDelaySeconds", Math.round(v))} fmt={(v) => `${Math.round(v)} s`} />
          </>
        )}
      </div>

      {full && prefs && (
        <>
          <div className="set-section">Résumés automatiques</div>
          <div className="set-card set-card-tight">
            <CheckRow label="Résumé du jour automatique" checked={prefs.autoSummaryDay} onChange={(v) => setPref("autoSummaryDay", v)} />
            <div className="set-divline" />
            <CheckRow label="Résumé de semaine partiel automatique" checked={prefs.autoSummaryWeekPartial} onChange={(v) => setPref("autoSummaryWeekPartial", v)} />
            <div className="set-divline" />
            <CheckRow label="Résumé de semaine automatique" checked={prefs.autoSummaryWeek} onChange={(v) => setPref("autoSummaryWeek", v)} />
            <div className="set-divline" />
            <CheckRow label="Remplacer le résumé à chaque génération" desc="Sinon, conserver et numéroter" checked={prefs.replaceSummary} onChange={(v) => setPref("replaceSummary", v)} />
            <div className="set-divline" />
            <CheckRow label="Supprimer les résumés intermédiaires" desc="Une fois le résumé hebdomadaire final généré" checked={prefs.autoDeleteIntermediateSummaries} onChange={(v) => setPref("autoDeleteIntermediateSummaries", v)} />
          </div>

          <div className="set-section">Rapports et e-mails</div>
          <div className="set-card set-card-tight">
            <CheckRow label="Rapport quotidien par e-mail" checked={prefs.dailyReportEnabled} onChange={(v) => setPref("dailyReportEnabled", v)} />
            {prefs.dailyReportEnabled && (
              <>
                <input className="textinput" type="email" placeholder="adresse@exemple.com" value={prefs.dailyReportEmail ?? ""} onChange={(e) => setPref("dailyReportEmail", e.target.value)} />
                <div className="set-row">
                  <span className="set-label">Heure d'envoi</span>
                  <input className="textinput" style={{ width: 130, margin: 0 }} type="time" value={time5(prefs.dailyReportTime)} onChange={(e) => setPref("dailyReportTime", e.target.value)} />
                </div>
                <CheckRow label="Joindre le résumé de la semaine courante" checked={prefs.dailyReportIncludeWeek} onChange={(v) => setPref("dailyReportIncludeWeek", v)} />
              </>
            )}
            <div className="set-divline" />
            <CheckRow label="Rapport hebdomadaire par e-mail" checked={prefs.weeklyReportEnabled} onChange={(v) => setPref("weeklyReportEnabled", v)} />
            {prefs.weeklyReportEnabled && (
              <>
                <input className="textinput" type="email" placeholder="adresse@exemple.com" value={prefs.weeklyReportEmail ?? ""} onChange={(e) => setPref("weeklyReportEmail", e.target.value)} />
                <div className="set-row">
                  <span className="set-label">Heure (dimanche)</span>
                  <input className="textinput" style={{ width: 130, margin: 0 }} type="time" value={time5(prefs.weeklyReportTime)} onChange={(e) => setPref("weeklyReportTime", e.target.value)} />
                </div>
              </>
            )}
            <div className="set-divline" />
            <CheckRow label="Retranscription quotidienne par e-mail" checked={prefs.dailyTranscriptionEnabled} onChange={(v) => setPref("dailyTranscriptionEnabled", v)} />
            {prefs.dailyTranscriptionEnabled && (
              <>
                <input className="textinput" type="email" placeholder="adresse@exemple.com" value={prefs.dailyTranscriptionEmail ?? ""} onChange={(e) => setPref("dailyTranscriptionEmail", e.target.value)} />
                <div className="set-row">
                  <span className="set-label">Heure d'envoi</span>
                  <input className="textinput" style={{ width: 130, margin: 0 }} type="time" value={time5(prefs.dailyTranscriptionTime)} onChange={(e) => setPref("dailyTranscriptionTime", e.target.value)} />
                </div>
              </>
            )}
          </div>

          <div className="set-section">Prompts système</div>
          <div className="set-card">
            <PromptField label="Résumé du jour" value={prefs.promptSummaryDay} onChange={(v) => setPref("promptSummaryDay", v)} />
            <div className="set-divline" />
            <PromptField label="Résumé de semaine partiel" value={prefs.promptSummaryWeekPartial} onChange={(v) => setPref("promptSummaryWeekPartial", v)} />
            <div className="set-divline" />
            <PromptField label="Résumé de semaine" value={prefs.promptSummaryWeek} onChange={(v) => setPref("promptSummaryWeek", v)} />
            <div className="set-divline" />
            <PromptField label="Résumé de note" value={prefs.promptSummaryStandalone} onChange={(v) => setPref("promptSummaryStandalone", v)} />
          </div>

          <div className="set-section">Rétention et stockage</div>
          <div className="set-card">
            <SettingSlider
              label="Rétention audio (jours)"
              value={retentionDays}
              min={1}
              max={365}
              step={1}
              onChange={(v) => {
                const d = Math.round(v);
                setRetentionDays(d);
                api.updateRetention(d).catch(() => undefined);
              }}
              fmt={(v) => `${Math.round(v)} j`}
            />
            {limits && (
              <>
                <div className="set-divline" />
                <div className="set-row">
                  <span className="set-label">Quota de transcription</span>
                  <span className="set-desc">{limits.quotaMinutes} min/mois (lecture seule)</span>
                </div>
                <div className="set-row">
                  <span className="set-label">Quota de volume audio</span>
                  <span className="set-desc">{(limits.quotaAudioBytes / 1e9).toFixed(1)} Go (lecture seule)</span>
                </div>
                <div className="set-row">
                  <span className="set-label">Délai de corbeille</span>
                  <span className="set-desc">{limits.trashRetentionDays} j (lecture seule)</span>
                </div>
              </>
            )}
          </div>

        </>
      )}

      {apparence}

      {full && (
        <>
          <div className="set-section">Corbeille</div>
          <TrashPanel onChanged={onChanged} />

          <div className="set-section">Zone de danger</div>
          <div className="set-card danger-card">
            <div className="set-row set-row-col">
              <span className="set-label">Tout supprimer</span>
              <span className="set-desc">Réinitialise toutes vos données. Saisissez « supprimer tout » pour confirmer.</span>
              <input className="textinput" type="text" placeholder="supprimer tout" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
              <button
                className="danger-btn"
                disabled={confirmText !== "supprimer tout"}
                onClick={async () => {
                  setResetMsg(null);
                  try {
                    await api.accountReset(confirmText);
                    setConfirmText("");
                    setResetMsg("Toutes vos données ont été supprimées.");
                    onChanged();
                  } catch {
                    setResetMsg("Suppression impossible.");
                  }
                }}
              >
                Tout supprimer
              </button>
              {resetMsg && <span className="editwin-flash">{resetMsg}</span>}
            </div>
          </div>
        </>
      )}

      <div className="set-section">Compte</div>
      <div className="set-card">
        <button className="logoutbtn" onClick={onLogout}>
          <Icons.Logout size={18} /> Se déconnecter
        </button>
      </div>

      <div className="set-foot">Pensieve</div>
    </Fragment>
  );
}
